import type { ChildGuideItem } from '@tunein/web-api-client';
import { isTopic, lotame } from '@tunein/web-common';
import {
  type LoadOptions,
  PlayerManager,
  type PlayerManagerOptions,
} from '@tunein/web-player-manager';
import { registerPlayerManagerEvents } from '@tunein/web-player-manager/react';
import { Singleton, type SingletonInstance } from '@tunein/web-utils';
import { isNotNil, pick } from 'es-toolkit';
import type { History } from 'history';
import isEmpty from 'lodash/isEmpty';
import { fbPixelCustomEvents, fbTrack } from 'src/client/analytics/facebook/fbTrack';
import { api } from 'src/client/api';
import { storeSingleton } from 'src/client/store/storeSingleton';
import { stores } from 'src/client/stores';
import { getSearchParam } from 'src/client/utils/getSearchParam.ts';
import {
  determineInAppMessageDialogRequestBehavior,
  openDownloadAppDialogAndTrack,
  openNowPlayingDialog,
  openUpsellAndTrackActivity,
} from 'src/common/actions/dialog';
import { logClientError, logFeatureActivity } from 'src/common/actions/logging';
import canShowInstallAppDialog from 'src/common/actions/utils/dialog/canShowInstallAppDialog';
import createAndOpenSubscribeLink from 'src/common/components/utils/createAndOpenSubscribeLink';
import feature from 'src/common/constants/analytics/categoryActionLabel/feature';
import { labels } from 'src/common/constants/analytics/categoryActionLabel/subscribe';
import { BRAZE_PLAY_EVENT } from 'src/common/constants/braze';
import { DISPLAY_ADS_LIBRARY_ENABLED } from 'src/common/constants/experiments/ads';
import { BYPASS_UPSELL_DIALOG_ENABLED } from 'src/common/constants/experiments/dialog';
import { PLAY_OPEN_IN_APP_ENABLED } from 'src/common/constants/experiments/redirect';
import {
  selectBranchUrl,
  selectFirstVisitStartTime,
  selectIsBrazeInitialized,
  selectIsMobile,
  selectOneTrust,
} from 'src/common/selectors/app';
import { selectCountryCode } from 'src/common/selectors/config';
import { selectIsNowPlayingDialogOpen } from 'src/common/selectors/dialog';
import { selectBreadcrumbId } from 'src/common/selectors/reporting';
import { brazeLogCustomEvent } from 'src/common/utils/braze/brazeHelper';
import { isInDiscordIFrame } from 'src/common/utils/discord';
import { PLATFORM_ENV_SHORTHAND } from 'src/common/utils/environment';
import getGuideItemPathname from 'src/common/utils/guideItem/getGuideItemPathname';
import { isProfile } from 'src/common/utils/guideItemTypes';
import { isGdprCountry } from 'src/common/utils/isGdprCountry';
import { mintSingleton } from '../mint';
import { popoutPlayer } from '../popout';
import { buildConsentData } from './utils';

type PlayerOptions = Required<PlayerManagerOptions>['playerOptions'];

export type PlayerSingletonOptions = {
  history: History;
  playerOptions?: Pick<PlayerOptions, 'volume'>;
} & Pick<PlayerManagerOptions, 'locale' | 'localizations' | 'enableFetchAutoplayGuideItem'>;

export class PlayerSingleton extends Singleton<PlayerManager> {
  #didUseSearchParam = false;
  #canAutoplay = true;
  #history: History | null = null;
  #autoplayGuideItem: ChildGuideItem | null = null;

  async init(options: PlayerSingletonOptions) {
    const { locale, localizations, enableFetchAutoplayGuideItem, history, playerOptions } = options;
    const state = storeSingleton.state;
    this.#history = history;

    const playerManager = new PlayerManager({
      api: api.instance,
      playerOptions: {
        volume: playerOptions?.volume,
      },
      locale,
      localizations,
      enableFetchAutoplayGuideItem,
      appSessionData: {
        isInDiscordIframe: isInDiscordIFrame(),
        isGdprGeo: isGdprCountry(selectCountryCode(state)),
        breadcrumbId: selectBreadcrumbId(state),
        env: PLATFORM_ENV_SHORTHAND,
        firstVisitTimestampMs: selectFirstVisitStartTime(state),
      },
    });

    // IMPORTANT: must be called prior to init below so that we can capture the autoplay metadata, if available
    registerPlayerManagerEvents(playerManager, stores.playerStore.getState());

    await playerManager.init();
    popoutPlayer.init(playerManager);

    this.instance = playerManager;

    this.#setupListeners();
  }

  /**
   * Toggle to ensure that an autoplay action won't override a user-initiated play request
   */
  get canAutoplay() {
    return this.#canAutoplay;
  }

  onClick = async (guideItem?: ChildGuideItem, options: LoadOptions = {}) => {
    this.#canAutoplay = false;

    if (!this.isReady) {
      return;
    }

    if (options?.isUserInitiated === false && getSearchParam('disableAutoplay')) {
      return;
    }

    const guideItemToPlay = this.#getGuideItemToPlay(guideItem);

    if (guideItemToPlay) {
      const canContinue = this.#handleShortCircuits(guideItemToPlay, options);

      if (canContinue) {
        this.#loadAndPlayGuideItem(guideItemToPlay as ChildGuideItem, options);
      }

      return;
    }

    // Prevents programmatic requests (e.g., async autoplay) from interrupting user-initiated playback
    if (guideItem && options?.isUserInitiated === false) {
      return;
    }

    if (this.instance.isPlaying()) {
      this.instance.isUnboundedStream() ? this.instance.stop() : this.instance.pause();
      return;
    }

    if (this.instance.isPaused()) {
      this.instance.play();
    }
  };

  // Convenience wrapper to allow passing reference without consuming args
  playNext = () => {
    return this.instance.playNext();
  };

  seekTo = (seekTime: number) => {
    stores.playerStore.getState().setCurrentTime(seekTime);
    this.instance.seekTo(seekTime);
  };

  seekBackward(seekOffset: number) {
    this.instance.seekBackward(seekOffset);
  }

  seekForward(seekOffset: number) {
    this.instance.seekForward(seekOffset);
  }

  async setAppSessionData(appSessionData: Parameters<PlayerManager['setAppSessionData']>[0]) {
    await this.ready;

    this.instance.setAppSessionData(appSessionData);
  }

  /**
   * Overrides the start position of the topic and is only valid for the initial load of the app session
   */
  get #positionFromSearchParam() {
    const position = this.#didUseSearchParam ? 0 : +getSearchParam('st');

    this.#didUseSearchParam = true;

    return position;
  }

  #setupListeners() {
    const { setCurrentGuideItem } = stores.playerStore.getState();
    const { setGuideItemPathname } = stores.appStore.getState();
    let oneTrust = selectOneTrust(storeSingleton.state);

    storeSingleton.subscribe(() => {
      const previousOneTrust = oneTrust;
      oneTrust = selectOneTrust(storeSingleton.state);

      if (previousOneTrust !== oneTrust) {
        buildConsentData().then((consentData) => {
          this.instance.setAppSessionData({ consentData });
        });
      }
    });

    this.instance.on('metadata', (metadata) => {
      lotame.addInterests(metadata);

      // TODO: investiaget where this "popup" data originates (NP?). Might need to do some archeology on older Gemini Web commits.
      // We use it for stations with live seek (i.e. FOX News Radio) - display dialog about rewind and live listening.
      // @ts-expect-error
      const inAppMessageKey = metadata?.popup?.destinationInfo?.id;

      if (inAppMessageKey && !storeSingleton.state.dialog.shouldInAppMessageDialogOpen) {
        storeSingleton.dispatch(
          determineInAppMessageDialogRequestBehavior(inAppMessageKey, metadata.primaryGuideId),
        );
      }
    });

    this.instance.on('nextguideitem', async (nextGuideItem) => {
      setGuideItemPathname(getGuideItemPathname(nextGuideItem));
    });

    // Keep Mint companions wired for the control cohort. The display-ads library subscribes
    // directly to PlayerManager in the treatment cohort.
    this.instance.on('companionAds', ({ companionAds, requestId, provider }) => {
      if (api.getConfigSetting(DISPLAY_ADS_LIBRARY_ENABLED, false)) {
        return;
      }

      mintSingleton.instance?.onCompanionAdsLoaded({
        companions: companionAds,
        requestId,
        adProvider: provider,
      });
    });

    const resetDisplayAdSlots = () => {
      if (!api.getConfigSetting(DISPLAY_ADS_LIBRARY_ENABLED, false)) {
        mintSingleton.instance?.resetDisplayAdSlots();
      }
    };

    this.instance.on('adcomplete', resetDisplayAdSlots);
    this.instance.on('adskipped', resetDisplayAdSlots);
    this.instance.on('adplaybackcancelled', resetDisplayAdSlots);

    // Non-topics that end with a profile next guide id should redirect to the profile page.
    this.instance.on('ended', async () => {
      const { currentGuideItem } = stores.playerStore.getState();

      if (
        !isTopic(currentGuideItem?.playableGuideId) &&
        isProfile(this.instance.currentStream?.nextGuideId)
      ) {
        this.#history?.replace(`/radio/${this.instance.currentStream.nextGuideId}/`);
      }
    });

    this.instance.on('topicguideitem', (guideItem) => setCurrentGuideItem(guideItem));

    // TODO: add this state to the React store from Player Manager
    this.instance.on('autoplayguideitem', (guideItem: ChildGuideItem) => {
      this.#autoplayGuideItem = guideItem;
    });
  }

  #getCachedPosition(guideItem: ChildGuideItem) {
    const positionFromSearchParam = this.#positionFromSearchParam;

    if (positionFromSearchParam) {
      return positionFromSearchParam;
    }

    const { time, percent } =
      stores.playerStore.getState().playbackProgressHistory[guideItem.playableGuideId as string] ||
      {};

    return percent === 100 ? 0 : time;
  }

  #sendPreLoadMetrics(guideItem: ChildGuideItem, options?: LoadOptions) {
    fbTrack(fbPixelCustomEvents.StartPlay, {
      guideId: guideItem.playableGuideId,
      isAutoplayed: isNotNil(options?.isUserInitiated) ? !options.isUserInitiated : false,
    });

    const isAutoPlayingRecents = guideItem === this.#autoplayGuideItem;

    if (isAutoPlayingRecents) {
      const { actions, labels: featureLabel } = feature;
      storeSingleton.dispatch(logFeatureActivity(actions.autoPlay, featureLabel.playMiniPlayer));
    }
  }

  #sendPostLoadMetrics(guideItem: ChildGuideItem) {
    const state = storeSingleton.state;
    const isBrazeInitialized = selectIsBrazeInitialized(state);

    if (isBrazeInitialized) {
      try {
        const { metadata } = stores.playerStore.getState();

        if (!metadata) {
          return;
        }

        // By sending this custom event to Braze triggers to show the In-App Message. Note: Make sure it happens after the preroll has finished playing.
        brazeLogCustomEvent(BRAZE_PLAY_EVENT, {
          guideId: guideItem.isTopic ? guideItem.parentGuideId : guideItem.playableGuideId,
          boostEnabledStation: !isEmpty(metadata.boost),
          title: metadata?.header?.title || metadata?.title,
          ...metadata?.classification,
          ...(guideItem.isTopic && {
            topicId: guideItem.playableGuideId,
          }),
        });
      } catch (err) {
        storeSingleton.dispatch(
          logClientError({
            message: `Braze Custom Event: ${BRAZE_PLAY_EVENT} Error`,
            context: {
              error: err,
            },
          }),
        );
        // NOTE: Don't break app functionality for Braze
      }
    }
  }

  #handleShortCircuits(guideItem: ChildGuideItem, options?: LoadOptions) {
    if (guideItem.isLocked && options?.isUserInitiated !== false) {
      this.#handleSubscribeAction(guideItem);
      return;
    }

    if (!guideItem.isPlayable) {
      return;
    }

    if (this.#handleBranchRedirect()) {
      return;
    }

    return true;
  }

  #handleBranchRedirect() {
    const state = storeSingleton.state;

    // on attempt to play, open via branch deep link
    const isMobile = selectIsMobile(state);
    const branchUrl = selectBranchUrl(state);
    const playOpenInAppStore = api.getConfigSetting(PLAY_OPEN_IN_APP_ENABLED);

    if (isMobile && playOpenInAppStore && branchUrl) {
      window.location.replace(branchUrl);

      return true;
    }
  }

  #handleSubscribeAction(guideItem: ChildGuideItem) {
    // Don't disrupt existing playback, but do short-circuit in-flight playback requests that
    // may cause a video preroll to open and disrupt any upsell opportunities
    if (!this.instance.isPlaying() && !this.instance.isPaused()) {
      player.stop();
    }

    if (api.getConfigSetting(BYPASS_UPSELL_DIALOG_ENABLED, false)) {
      createAndOpenSubscribeLink(guideItem, location, null, 'tune.action');
      return;
    }

    return storeSingleton.dispatch(
      openUpsellAndTrackActivity(labels.profileUpsellDialog, guideItem.playableGuideId),
    );
  }

  #getGuideItemToPlay(guideItem?: ChildGuideItem) {
    const { error, currentGuideItem, metadata, popout } = stores.playerStore.getState();
    const isNewGuideItem =
      guideItem && guideItem.playableGuideId !== currentGuideItem?.playableGuideId;
    const isCurrentGuideItemIdle =
      currentGuideItem && this.instance.isIdle() && !metadata?.rejectReason && !popout.showButton;
    const shouldLoadAndPlayGuideItem = isNewGuideItem || isCurrentGuideItemIdle || Boolean(error);
    const guideItemToPlay = guideItem || currentGuideItem;

    if (shouldLoadAndPlayGuideItem) {
      return guideItemToPlay as ChildGuideItem;
    }
  }

  async #loadAndPlayGuideItem(guideItem: ChildGuideItem, options?: LoadOptions) {
    const { setCurrentGuideItem, setPopoutState } = stores.playerStore.getState();
    const { setGuideItemPathname } = stores.appStore.getState();

    setCurrentGuideItem(guideItem);
    setPopoutState({ showButton: false });

    if (guideItem.routePath) {
      setGuideItemPathname(guideItem.routePath);
    } else {
      setGuideItemPathname(getGuideItemPathname(guideItem, undefined));

      // TODO: delete once we've confirmed that routePath is always defined here
      api.telemetry.logError({
        message: 'Missing guideItem.routePath',
        context: pick(guideItem, [
          'guideId',
          'parentGuideId',
          'title',
          'containerGuideId',
          'playableGuideId',
        ]),
      });
    }

    this.#sendPreLoadMetrics(guideItem, options);
    this.#updateAppSessionData();
    await this.instance.load(guideItem, {
      ...options,
      position: this.#getCachedPosition(guideItem),
    });

    if (!this.instance.isLoaded()) {
      return;
    }

    this.#playSideEffects(guideItem);
    await this.instance.play();
  }

  #playSideEffects(guideItem: ChildGuideItem) {
    const state = storeSingleton.state;
    const isNowPlayingDialogOpen = selectIsNowPlayingDialogOpen(state);

    if (!isNowPlayingDialogOpen && canShowInstallAppDialog(state)) {
      storeSingleton.dispatch(openDownloadAppDialogAndTrack());
    } else {
      storeSingleton.dispatch(openNowPlayingDialog());
    }

    this.#sendPostLoadMetrics(guideItem);
  }

  async #updateAppSessionData() {
    const state = storeSingleton.state;

    this.instance.setAppSessionData({
      breadcrumbId: selectBreadcrumbId(state),
    });
  }
}

export const player = new PlayerSingleton() as SingletonInstance<PlayerManager, PlayerSingleton>;
